/**
 * iGaming player segmentation — data layer (OPUS DASH-158 / feature #158).
 *
 * Typed wrappers over `apiClient` for the four endpoints exposed by the API
 * route at `/api/v1/igaming-player-segmentation`:
 *
 *   GET    /segments
 *   GET    /segments/:tier/players?limit&offset
 *   GET    /players/:player_id
 *   PUT    /players/:player_id/activity
 *
 * No React / no TanStack imports here — the page imports these helpers and
 * tests assert behaviour without bringing in a DOM / hooks runtime. Tier
 * threshold constants + the pure `classifyTier` helper mirror the API's
 * business rules so the form can show the user a live preview of the tier a
 * submission will land in.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Tier constants (mirror the API's contract — keep in sync).
// ---------------------------------------------------------------------------

export const PLAYER_TIERS = ["vip", "casual", "dormant"] as const;
export type PlayerTier = (typeof PLAYER_TIERS)[number];

export const VIP_NGR_CENTS_THRESHOLD = 100_000; // $1,000.00
export const VIP_DEPOSIT_CENTS_THRESHOLD = 500_000; // $5,000.00
export const DORMANT_SECONDS_THRESHOLD = 30 * 24 * 60 * 60; // 30 days

// ---------------------------------------------------------------------------
// Public row shapes
// ---------------------------------------------------------------------------

export interface PlayerRow {
	id: string;
	player_id: string;
	lifetime_ngr_cents: number;
	lifetime_deposits_cents: number;
	lifetime_bets_cents: number;
	session_count: number;
	last_activity_ts: number;
	tier: PlayerTier;
	created_at: number;
	updated_at: number;
}

export interface SegmentTileRow {
	tier: PlayerTier;
	player_count: number;
	total_ltv_cents: number;
	total_deposits_cents: number;
	total_bets_cents: number;
	total_sessions: number;
}

export interface SegmentsResponse {
	tiers: SegmentTileRow[];
	generated_at: number;
}

export interface SegmentPlayersResponse {
	tier: PlayerTier;
	players: PlayerRow[];
	count: number;
	total: number;
	limit: number;
	offset: number;
}

// ---------------------------------------------------------------------------
// API paths
// ---------------------------------------------------------------------------

export const playerSegmentationPaths = {
	root: "/igaming-player-segmentation",
	segments: "/igaming-player-segmentation/segments",
	segmentPlayers: (tier: PlayerTier) =>
		`/igaming-player-segmentation/segments/${tier}/players`,
	player: (playerId: string) =>
		`/igaming-player-segmentation/players/${encodeURIComponent(playerId)}`,
	playerActivity: (playerId: string) =>
		`/igaming-player-segmentation/players/${encodeURIComponent(playerId)}/activity`,
} as const;

// ---------------------------------------------------------------------------
// Pure classifier — mirrors the API for live tier preview.
// ---------------------------------------------------------------------------

export interface ClassifyInput {
	lifetime_ngr_cents: number;
	lifetime_deposits_cents: number;
	last_activity_ts: number;
	now_seconds: number;
}

export function classifyTier(input: ClassifyInput): PlayerTier {
	const isVip =
		input.lifetime_ngr_cents >= VIP_NGR_CENTS_THRESHOLD ||
		input.lifetime_deposits_cents >= VIP_DEPOSIT_CENTS_THRESHOLD;
	if (isVip) return "vip";
	const idleSeconds = input.now_seconds - input.last_activity_ts;
	if (idleSeconds >= DORMANT_SECONDS_THRESHOLD) return "dormant";
	return "casual";
}

// ---------------------------------------------------------------------------
// Form validation — symmetrical to the API's body parser so the page can
// surface errors inline without a round-trip. Mirrors the safeParse shape
// other dash features use.
// ---------------------------------------------------------------------------

export interface ActivityFormInput {
	playerId?: unknown;
	lifetimeNgrCents?: unknown;
	lifetimeDepositsCents?: unknown;
	lifetimeBetsCents?: unknown;
	sessionCount?: unknown;
	lastActivityTs?: unknown;
}

export interface ActivityFormData {
	playerId: string;
	lifetimeNgrCents: number;
	lifetimeDepositsCents: number;
	lifetimeBetsCents: number;
	sessionCount: number;
	lastActivityTs: number;
}

export type ActivityFormParseResult =
	| { success: true; data: ActivityFormData }
	| { success: false; errors: Record<string, string> };

function toNonNegInt(raw: unknown): number | null {
	if (typeof raw === "number") {
		if (!Number.isFinite(raw) || !Number.isInteger(raw) || raw < 0) return null;
		return raw;
	}
	if (typeof raw === "string") {
		const s = raw.trim();
		if (!s) return null;
		const n = Number(s);
		if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
		return n;
	}
	return null;
}

export const activityFormSchema = {
	safeParse(input: ActivityFormInput): ActivityFormParseResult {
		const errors: Record<string, string> = {};

		const playerIdRaw =
			typeof input.playerId === "string" ? input.playerId.trim() : "";
		if (!playerIdRaw) {
			errors.playerId = "Player ID is required.";
		} else if (playerIdRaw.length > 128) {
			errors.playerId = "Player ID must be 128 characters or fewer.";
		}

		const ngr = toNonNegInt(input.lifetimeNgrCents);
		if (ngr === null) errors.lifetimeNgrCents = "Must be a non-negative integer.";

		const deposits = toNonNegInt(input.lifetimeDepositsCents);
		if (deposits === null)
			errors.lifetimeDepositsCents = "Must be a non-negative integer.";

		const bets = toNonNegInt(input.lifetimeBetsCents);
		if (bets === null)
			errors.lifetimeBetsCents = "Must be a non-negative integer.";

		const sessions = toNonNegInt(input.sessionCount);
		if (sessions === null) errors.sessionCount = "Must be a non-negative integer.";

		const lastTs = toNonNegInt(input.lastActivityTs);
		if (lastTs === null) errors.lastActivityTs = "Must be a Unix seconds timestamp.";

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				playerId: playerIdRaw,
				lifetimeNgrCents: ngr!,
				lifetimeDepositsCents: deposits!,
				lifetimeBetsCents: bets!,
				sessionCount: sessions!,
				lastActivityTs: lastTs!,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// API fetchers
// ---------------------------------------------------------------------------

export async function fetchSegments(): Promise<SegmentsResponse> {
	try {
		return await apiClient.get<SegmentsResponse>(
			playerSegmentationPaths.segments,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return {
				tiers: PLAYER_TIERS.map((tier) => ({
					tier,
					player_count: 0,
					total_ltv_cents: 0,
					total_deposits_cents: 0,
					total_bets_cents: 0,
					total_sessions: 0,
				})),
				generated_at: Math.floor(Date.now() / 1000),
			};
		}
		throw err;
	}
}

export async function fetchSegmentPlayers(
	tier: PlayerTier,
	opts: { limit?: number; offset?: number } = {},
): Promise<SegmentPlayersResponse> {
	const { limit, offset } = opts;
	try {
		return await apiClient.get<SegmentPlayersResponse>(
			playerSegmentationPaths.segmentPlayers(tier),
			{
				query: {
					limit: typeof limit === "number" ? limit : undefined,
					offset: typeof offset === "number" ? offset : undefined,
				},
			},
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return {
				tier,
				players: [],
				count: 0,
				total: 0,
				limit: limit ?? 50,
				offset: offset ?? 0,
			};
		}
		throw err;
	}
}

export async function fetchPlayer(playerId: string): Promise<PlayerRow | null> {
	try {
		const res = await apiClient.get<{ player: PlayerRow }>(
			playerSegmentationPaths.player(playerId),
		);
		return res.player;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return null;
		}
		throw err;
	}
}

export async function upsertPlayerActivity(
	data: ActivityFormData,
): Promise<PlayerRow> {
	const res = await apiClient.put<{ player: PlayerRow }>(
		playerSegmentationPaths.playerActivity(data.playerId),
		{
			lifetime_ngr_cents: data.lifetimeNgrCents,
			lifetime_deposits_cents: data.lifetimeDepositsCents,
			lifetime_bets_cents: data.lifetimeBetsCents,
			session_count: data.sessionCount,
			last_activity_ts: data.lastActivityTs,
		},
	);
	return res.player;
}

// ---------------------------------------------------------------------------
// Formatters — exported so tests pin the human-readable output strings.
// ---------------------------------------------------------------------------

export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100).toLocaleString("en-US");
	const rem = (abs % 100).toString().padStart(2, "0");
	return `${sign}$${dollars}.${rem}`;
}

export function formatCount(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return Math.max(0, Math.floor(n)).toLocaleString("en-US");
}

export function tierLabel(tier: PlayerTier): string {
	switch (tier) {
		case "vip":
			return "VIP";
		case "casual":
			return "Casual";
		case "dormant":
			return "Dormant";
	}
}

export function tierDescription(tier: PlayerTier): string {
	switch (tier) {
		case "vip":
			return "High lifetime value; always treated as hospitality.";
		case "casual":
			return "Active in the last 30 days; not yet VIP.";
		case "dormant":
			return "No activity for 30+ days; candidate for re-activation.";
	}
}
